/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useState, useEffect } from 'react'
import Modal from '../Modal'
import styled from 'styled-components'
import Loader from '../shared/Loader'

const StyledDiv = styled.div`
  b {
    word-break: break-word;
    color: #595959;
  }
`

interface ModalConfirmationProps {
  titleMsg: string
  msg: React.ReactNode | string
  cancelTxt: string
  okTxt: string
  onSubmit: () => Promise<any> | void
  onCancel?: () => void
  loading?: Promise<any> | null
  show?: boolean
}

const ModalConfirmation: React.FC<ModalConfirmationProps> = ({
  titleMsg,
  msg,
  cancelTxt,
  okTxt,
  onSubmit,
  onCancel,
  loading: initialLoading = null,
  show: initialShow = true
}) => {
  const [isOpen, setIsOpen] = useState(initialShow)
  const [loading, setLoading] = useState<boolean>(false)

  useEffect(() => {
    if (initialLoading) {
      setLoading(true)
      initialLoading.then(() => setLoading(false)).catch(() => setLoading(false))
    }
  }, [initialLoading])

  const handleSubmit = async () => {
    try {
      const result = onSubmit()
      if (result && typeof result.then === 'function') {
        setLoading(true)
        await result
        setLoading(false)
      }
    } catch (error) {
      setLoading(false)
    }
    handleClose()
  }

  const handleCancel = () => {
    handleClose()
    if (onCancel) {
      onCancel()
    }
  }

  const handleClose = () => {
    setIsOpen(false)
  }

  return (
    <Modal show={isOpen} handleClose={handleCancel} title={titleMsg} size="md" isConfirmationDialog={true}>
      <Loader blocking={loading}>
        <Modal.Body style={{ padding: '15px', fontSize: '12px', lineHeight: '1.62857143', textAlign: 'left' }}>
          <StyledDiv id="6f86c896-415a-4df7-bd66-e58816766eff">{msg}</StyledDiv>
        </Modal.Body>
        <Modal.Footer>
          <button
            data-action={'cancel'}
            className="btn btn-chita btn-link"
            onClick={handleCancel}
            id="fc29d04b-3fe6-4ee0-9581-ea0c670cf54c"
            disabled={loading}
          >
            {cancelTxt}
          </button>
          <button
            data-action={'ok'}
            className="btn btn-primary text-capitalize"
            onClick={handleSubmit}
            id="66f15e7b-4aa4-4fdd-9b66-0d90e3be5107"
            disabled={loading}
          >
            {okTxt}
          </button>
        </Modal.Footer>
      </Loader>
    </Modal>
  )
}

export default ModalConfirmation
